import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { auth } from './firebase';
import { isDesktop } from './platform';
import { ensureLicenseToken } from './licenseToken';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';
const googleProvider = new GoogleAuthProvider();

/**
 * Where Firebase should send the user after they click "Verify email".
 * Without this, Firebase shows its own hosted "Your email is verified" page
 * and the user has to find their way back to scrivar.com manually.
 *
 * Firebase requires continueUrl to be on an authorized HTTPS domain. The
 * renderer's origin in Electron is `pdfsuite://app` which can't be authorized,
 * so desktop users get sent to the production website. Browser users go back
 * to whatever origin they're on (scrivar.com in prod; localhost only works if
 * the dev added it to the Firebase Auth authorized-domains list).
 *
 * Desktop adds `?from=app` so the web landing shows a "verified — return to the
 * Scrivar app" message instead of the web sign-in/code flow: the desktop app is
 * already polling for verification and binds the code automatically, so there's
 * nothing for the user to do in the browser.
 */
function verificationActionCodeSettings() {
  const url = isDesktop()
    ? 'https://scrivar.com/activate?from=app'
    : `${window.location.origin}/activate`;
  return { url, handleCodeInApp: false };
}

// --- Firebase Auth ---

/**
 * Sign a new account up.
 *
 * @param {string} email
 * @param {string} password
 * @param {{firstName?: string, lastName?: string, companyName?: string}} [profile]
 *   firstName is required when supplied (the signup form enforces it). All
 *   three are persisted to scrivar_accounts via PATCH /account so eSign
 *   emails and the Account page can render the user's actual name.
 */
export async function signUp(email, password, profile = {}) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);

  const firstName = (profile.firstName || '').trim();
  const lastName = (profile.lastName || '').trim();
  const companyName = (profile.companyName || '').trim();
  const displayName = [firstName, lastName].filter(Boolean).join(' ');

  // Set Firebase displayName so the next ID-token claim carries `name`.
  // authMiddleware reads this on the server and stores it in
  // scrivar_accounts.display_name as a fallback for accounts that never
  // PATCH /account (Google sign-in users land here too via a different
  // path, but Google already provides displayName).
  if (displayName) {
    await updateProfile(cred.user, { displayName }).catch(() => {});
  }

  // Non-fatal: the account already exists and the user can resend from the verify
  // screen. But do NOT swallow silently — a misconfig here (e.g. an unauthorized
  // continue-URL domain) otherwise looks like a successful "check your email" and
  // can hide for weeks. Log the real Firebase error code so it surfaces immediately.
  await sendEmailVerification(cred.user, verificationActionCodeSettings()).catch((err) => {
    console.error('[auth] sendEmailVerification failed on signup:', err?.code, err?.message);
  });
  // Force-refresh so the token carries the `name` claim from the updateProfile
  // above. registerAccount() is the first authed call the server sees, and it's
  // where the one-time welcome email fires — without the refresh the server gets
  // the pre-updateProfile cached token (no name) and the welcome greets a generic
  // "there" instead of the user's first name.
  const token = await cred.user.getIdToken(true);
  await registerAccount(token);
  await createSession(token);

  // Persist split first/last/company to the account row. Best-effort —
  // a failure here doesn't block sign-up (the user can edit on /account).
  if (firstName || lastName || companyName) {
    await fetch(`${API}/account`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        first_name: firstName || undefined,
        last_name: lastName,
        company_name: companyName,
      }),
    }).catch(() => {});
  }

  return cred.user;
}

export async function signIn(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const token = await cred.user.getIdToken();
  await createSession(token);
  // Deliberate sign-in on this device: (re)issue the offline license token so
  // "sign in once → log out → keep working" holds (no-op on web / no code).
  await ensureLicenseToken().catch(() => {});
  return cred.user;
}

export async function signInWithGoogle() {
  if (isDesktop() && window.electronAPI?.startGoogleOAuth) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Google sign-in timed out. Try again.'));
      }, 5 * 60 * 1000);

      const cleanup = () => clearTimeout(timeout);

      window.electronAPI.onGoogleOAuthComplete(async (customToken) => {
        cleanup();
        try {
          const cred = await signInWithCustomToken(auth, customToken);
          const token = await cred.user.getIdToken();
          await registerAccount(token);
          await createSession(token);
          await ensureLicenseToken().catch(() => {});
          resolve(cred.user);
        } catch (err) {
          reject(err);
        }
      });

      window.electronAPI.onGoogleOAuthError((errMsg) => {
        cleanup();
        reject(new Error(errMsg || 'Google sign-in failed.'));
      });

      window.electronAPI.startGoogleOAuth().catch((err) => {
        cleanup();
        reject(err);
      });
    });
  }

  // Web: use signInWithPopup
  const cred = await signInWithPopup(auth, googleProvider);
  const token = await cred.user.getIdToken();
  await registerAccount(token);
  await createSession(token);
  await ensureLicenseToken().catch(() => {}); // no-op on web (isDesktop guard)
  return cred.user;
}

export async function resendVerificationEmail() {
  const user = auth.currentUser;
  if (user) {
    await sendEmailVerification(user, verificationActionCodeSettings());
  }
}

export async function signOut() {
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (token) {
    await fetch(`${API}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }
  localStorage.removeItem('scrivar_session');
  if (window.electronAPI?.clearSession) {
    await window.electronAPI.clearSession().catch(() => {});
  }
  await firebaseSignOut(auth);
}

export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

export async function getIdToken() {
  return auth.currentUser?.getIdToken();
}

export function getCurrentUser() {
  return auth.currentUser;
}

// --- Our API calls ---

async function registerAccount(idToken) {
  const res = await fetch(`${API}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
  });
  return res.json();
}

export async function createSession(idToken) {
  const platform = window.electronAPI?.isDesktop
    ? (navigator.platform.includes('Win') ? 'win' : 'mac')
    : 'web';
  const res = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ platform }),
  });
  const data = await res.json();
  if (data?.sessionToken) {
    const sessionPayload = {
      token: data.sessionToken,
      tier: data.tier,
      email: data.email,
      displayName: data.displayName,
    };
    localStorage.setItem('scrivar_session', JSON.stringify(sessionPayload));
    // Mirror to Electron store for offline grace.
    if (window.electronAPI?.storeSession) {
      window.electronAPI.storeSession(sessionPayload).catch(() => {});
    }
  }
  return data;
}

export async function reclaimSession() {
  const token = await getIdToken();
  if (!token) return null;
  return createSession(token);
}
